// Face alignment across a gap: while a product is dragged, one of its
// faces lines up with the matching face of a nearby product or the room's
// walls when it comes close, across any gap, and holds there until pulled
// clearly away. A wall in range comes first, so a product flush against a
// wall shows it.
import * as THREE from 'three';
import {
    AXES,
    Axis,
    axisIndex,
} from 'components/customer/RoomPlanner/helpers/axes';
import {SurfaceFacts} from 'components/customer/RoomPlanner/helpers/snapping/types';
import {WallFacts} from 'components/customer/RoomPlanner/helpers/snapping/roomWalls';
import {ExteriorFace} from 'components/customer/RoomPlanner/helpers/componentGeometry';
import {FILLER_SNAPPING_DISTANCE} from 'components/customer/RoomPlanner/helpers/snapping/constants';

// How near on screen, in pixels, a face must come to another product's face
// to line up with it, and how far it may then pull away before it lets go.
export const ALIGN_ENTER_PX = 6;
export const ALIGN_RELEASE_PX = 12;

// How far away, in scene units (2 m), another product can be and still be
// lined up with. Products further off are not searched.
export const ALIGN_SEARCH_RADIUS = 400;

// Faces closer together than this (10 mm) are one plane, at the largest
// face's position. A door often sits a few millimetres in from the side of
// its carcase, or overhangs it; lining up with the door's thin edge rather
// than the carcase side would leave the carcases visibly off line.
export const CLOSE_FACES = 2;

// A view looking straight along an axis, as plan and the elevations do, is
// measured on its own plane: how far apart two products are is how far apart
// they look, with that axis left out. Their presets look exactly along one,
// so this allows only rounding error (about 0.08 degrees); any view turned
// off an axis is a 3D view.
const VIEW_AXIS = 1 - 1e-6;

// How much of a drag's movement must run along an axis for the face leading
// that way to win a tie with the trailing one. Below it the drag is crossing
// that axis rather than moving along it, and neither leads.
export const LEADING_SHARE = 0.3;

// Two planes this close in distance (0.05 mm) are as near as each other: the
// held one stays, else the leading face's wins.
const TIE = 0.01;

// An axis covering less than this share of the screen distance the
// clearest axis does points nearly into the screen, as depth does in an
// elevation: how near a face is along it can't be judged on screen, so
// nothing lines up along it.
const END_ON_SHARE = 0.2;

/**
 * How close a face must come to line up, and how far it may then pull away
 * before it lets go, per axis, in scene units.
 */
export interface FaceAlignmentRange {
    enter: (axis: Axis) => number;
    release: (axis: Axis) => number;
}

/**
 * What of the dragged product lines up: its solid box and its exterior faces,
 * where the drag puts it.
 */
export interface AlignmentBody {
    bbox: THREE.Box3;
    faces: readonly ExteriorFace[];
    // Its separate solid boxes, so its open corners stay open when it butts
    // into others; its box alone when not given.
    solids?: readonly THREE.Box3[];
}

/**
 * @param {SurfaceFacts} facts A product.
 * @return {AlignmentBody} Its solid box and exterior faces, as it stands.
 */
export const alignmentBodyOf = (facts: SurfaceFacts): AlignmentBody => ({
    bbox: facts.solidBBoxes.reduce(
        (union, bbox) => union.union(bbox),
        new THREE.Box3()
    ),
    faces: facts.exteriorFaces,
    solids: facts.solidBBoxes,
});

/**
 * @param {AlignmentBody} body A product's solid box and exterior faces.
 * @param {THREE.Vector3} offset How far to move them.
 * @return {AlignmentBody} A copy, moved.
 */
export const shiftBody = (
    body: AlignmentBody,
    offset: THREE.Vector3
): AlignmentBody => ({
    bbox: body.bbox.clone().translate(offset),
    solids: body.solids?.map((solid) => solid.clone().translate(offset)),
    faces: body.faces.map((face) => ({
        ...face,
        position: face.position + (face.axis === 'x' ? offset.x : offset.z),
        rect: face.rect.clone().translate(offset),
    })),
});

/**
 * An alignment a drag is holding: the other product's face plane, and which
 * of the dragged product's faces lines up with it.
 */
export interface HeldFaceAlignment {
    other: SurfaceFacts;
    axis: Axis;
    position: number;
    side: 'min' | 'max';
}

/**
 * An alignment for one move: what it holds, and what to add to the drag
 * target to line up.
 */
export interface FaceAlignment extends HeldFaceAlignment {
    offset: THREE.Vector3;
    distance: number;
    held: HeldFaceAlignment;
}

/**
 * @param {ExteriorFace} face A face.
 * @return {number} Its area.
 */
const areaOf = ({axis, rect}: ExteriorFace): number => {
    const size = rect.getSize(new THREE.Vector3());

    return size.y * (axis === 'x' ? size.z : size.x);
};

/**
 * @param {ExteriorFace[]} faces Faces across one axis.
 * @return {ExteriorFace} The largest of them.
 */
const largestOf = (faces: ExteriorFace[]): ExteriorFace =>
    faces.reduce((largest, face) =>
        areaOf(face) > areaOf(largest) ? face : largest
    );

/**
 * @param {ExteriorFace[]} faces Exterior faces.
 * @param {Axis} axis A floor axis.
 * @return {number[]} The positions of the planes they make across that
 * axis: faces within CLOSE_FACES of each other, in a run, are one plane at
 * the largest face's position.
 */
export const facePlanes = (
    faces: readonly ExteriorFace[],
    axis: Axis
): number[] => {
    const across = faces
        .filter((face) => face.axis === axis)
        .sort((a, b) => a.position - b.position);
    const runs: ExteriorFace[][] = [];
    across.forEach((face) => {
        const run = runs[runs.length - 1];
        if (run && face.position - run[run.length - 1].position < CLOSE_FACES) {
            run.push(face);
        } else {
            runs.push([face]);
        }
    });

    return runs.map((run) => largestOf(run).position);
};

/**
 * @param {SurfaceFacts} facts A product, or the walls.
 * @param {Axis} axis An axis.
 * @return {number[]} The positions of its face planes across that axis: its
 * exterior faces on the floor axes (facePlanes); on height, its top, its
 * bottom and the bottom of its solid body, above any legs.
 */
const planesOf = (facts: SurfaceFacts, axis: Axis): number[] =>
    axis === 'y'
        ? [
              facts.bbox.min.y,
              facts.bbox.max.y,
              Math.min(...facts.solidBBoxes.map((bbox) => bbox.min.y)),
          ]
        : facePlanes(facts.exteriorFaces, axis);

/**
 * Where the dragged product's face on one side is, for lining up: its
 * outermost faces looking that way, the largest of those within CLOSE_FACES
 * of the outermost, so a door overhanging its carcase doesn't decide where
 * the carcase lines up. Height, and a product with no such faces, use its
 * box.
 * @param {AlignmentBody} body The dragged product where the drag puts it.
 * @param {Axis} axis The axis.
 * @param {string} side Which side.
 * @return {number} The face's position along the axis.
 */
export const referencePlane = (
    body: AlignmentBody,
    axis: Axis,
    side: 'min' | 'max'
): number => {
    const index = axisIndex(axis);
    const edge =
        side === 'min'
            ? body.bbox.min.getComponent(index)
            : body.bbox.max.getComponent(index);
    if (axis === 'y') return edge;

    const looking = side === 'min' ? -1 : 1;
    const facing = body.faces.filter(
        (face) => face.axis === axis && face.side === looking
    );
    if (facing.length === 0) return edge;

    const outermost =
        side === 'min'
            ? Math.min(...facing.map(({position}) => position))
            : Math.max(...facing.map(({position}) => position));

    return largestOf(
        facing.filter(
            ({position}) => Math.abs(position - outermost) < CLOSE_FACES
        )
    ).position;
};

/**
 * @param {THREE.Vector3} viewDirection Which way the camera looks.
 * @return {Axis | null} The axis it looks straight along, near enough,
 * as plan and the elevations do; null for a view at an angle, as the 3D
 * views are.
 */
export const viewAxisOf = (viewDirection: THREE.Vector3): Axis | null => {
    const direction = viewDirection.clone().normalize();
    const along = AXES.find(
        (axis) => Math.abs(direction.getComponent(axisIndex(axis))) >= VIEW_AXIS
    );

    return along ?? null;
};

/**
 * @param {THREE.Box3} a A box.
 * @param {THREE.Box3} b Another.
 * @param {Axis | null} viewAxis The axis the view looks along, left out
 * so the gap is the one seen; null for the gap in the room.
 * @return {number} The gap between them, 0 where they overlap.
 */
const bboxGap = (a: THREE.Box3, b: THREE.Box3, viewAxis: Axis | null): number =>
    Math.hypot(
        ...[0, 1, 2]
            .filter(
                (index) => viewAxis === null || index !== axisIndex(viewAxis)
            )
            .map((index) =>
                Math.max(
                    0,
                    a.min.getComponent(index) - b.max.getComponent(index),
                    b.min.getComponent(index) - a.max.getComponent(index)
                )
            )
    );

/**
 * @param {AlignmentBody} moving The dragged product at the target.
 * @param {HeldFaceAlignment} held What to line up.
 * @return {FaceAlignment} The alignment, with the offset that lands it.
 */
const alignmentFor = (
    moving: AlignmentBody,
    held: HeldFaceAlignment
): FaceAlignment => {
    const index = axisIndex(held.axis);
    const from = referencePlane(moving, held.axis, held.side);
    const offset = new THREE.Vector3().setComponent(
        index,
        held.position - from
    );

    return {...held, offset, distance: Math.abs(held.position - from), held};
};

/**
 * The single nearest face plane of another product or the walls that one of
 * the dragged product's faces can line up with, across any gap, a wall's
 * before any product's. The alignment held last move stays while within its
 * release range and nothing else is nearer, so it doesn't hop between planes
 * as near as each other, but gives way to a nearer one at once, so faces
 * close together can each be lined up with (CO-5858); a wall in range takes
 * over from a product's held face.
 * @param {AlignmentBody} moving The dragged product at the target.
 * @param {SurfaceFacts[]} others The products and walls it may line up
 * with.
 * @param {Axis[]} axes The axes it may line up along: both floor axes on
 * a free drag, only the held axis under a hold. Height only under a hold to
 * height, as nothing else moves a product up or down.
 * @param {FaceAlignmentRange} range How near it takes and how far it lets go.
 * @param {HeldFaceAlignment | null} held The alignment held last move.
 * @param {THREE.Vector3 | null} [direction] Which way the drag is moving.
 * Of two faces as near a plane as each other, the one leading the drag along
 * an axis it is clearly moving along lines up.
 * @param {Axis | null} [viewAxis] The axis a plan or elevation view looks
 * along (viewAxisOf). How far off another product is is then measured on the
 * view, so products across the room that look side by side are searched.
 * @return {FaceAlignment | null} The alignment, or null when none is near.
 */
export const findFaceAlignment = (
    moving: AlignmentBody,
    others: SurfaceFacts[],
    axes: Axis[],
    range: FaceAlignmentRange,
    held: HeldFaceAlignment | null,
    direction: THREE.Vector3 | null = null,
    viewAxis: Axis | null = null
): FaceAlignment | null => {
    const movedFar = direction ? direction.length() : 0;
    const leads = ({axis, side}: HeldFaceAlignment): boolean => {
        if (!direction || movedFar === 0) return false;

        const along = direction.getComponent(axisIndex(axis));
        if (Math.abs(along) < LEADING_SHARE * movedFar) return false;

        return side === (along > 0 ? 'max' : 'min');
    };

    // The nearest plane in range, by any face, a wall's before any
    // product's; of two as near, the leading face's.
    const isWall = (found: HeldFaceAlignment | null) =>
        found?.other instanceof WallFacts;
    // Cast: TypeScript does not follow the assignment in the callback.
    let best = null as HeldFaceAlignment | null;
    let bestDistance = Infinity;
    others
        .filter(
            (other) =>
                bboxGap(moving.bbox, other.bbox, viewAxis) <=
                ALIGN_SEARCH_RADIUS
        )
        .forEach((other) => {
            axes.forEach((axis) => {
                const enter = range.enter(axis);
                const sides = (['min', 'max'] as const).map(
                    (side) =>
                        [side, referencePlane(moving, axis, side)] as const
                );
                planesOf(other, axis).forEach((position) => {
                    sides.forEach(([side, from]) => {
                        const distance = Math.abs(position - from);
                        if (distance >= enter) return;

                        const found = {other, axis, position, side};
                        if (isWall(best) && !isWall(found)) return;
                        const nearer =
                            (isWall(found) && !isWall(best)) ||
                            distance < bestDistance - TIE;
                        const tied =
                            !nearer &&
                            distance <= bestDistance + TIE &&
                            best !== null &&
                            leads(found) &&
                            !leads(best);
                        if (!nearer && !tied) return;

                        best = found;
                        bestDistance = distance;
                    });
                });
            });
        });

    if (held !== null && axes.includes(held.axis)) {
        const kept = alignmentFor(moving, held);
        if (
            kept.distance < range.release(held.axis) &&
            (isWall(held) || !isWall(best)) &&
            (kept.distance <= bestDistance + TIE ||
                (isWall(held) && !isWall(best)))
        ) {
            return kept;
        }
    }

    return best ? alignmentFor(moving, best) : null;
};

/**
 * The range for face alignment, set in screen pixels so it feels the same at
 * any zoom, and never wider than the flush snap's own range (110 mm). Nothing
 * lines up along an axis pointing nearly into the screen (END_ON_SHARE).
 * @param {THREE.Vector3} perUnit How many pixels one scene unit along each
 * axis covers on screen where the dragged product is (pixelsPerUnit).
 * @return {FaceAlignmentRange} The range, per axis, in scene units.
 */
export const screenAlignmentRange = (
    perUnit: THREE.Vector3
): FaceAlignmentRange => {
    const clearest = Math.max(perUnit.x, perUnit.y, perUnit.z);
    const inScene = (pixels: number) => (axis: Axis) => {
        const units = perUnit.getComponent(axisIndex(axis));
        if (units <= END_ON_SHARE * clearest) return 0;

        return Math.min(pixels / units, FILLER_SNAPPING_DISTANCE);
    };

    return {enter: inScene(ALIGN_ENTER_PX), release: inScene(ALIGN_RELEASE_PX)};
};
